import * as Sentry from "@sentry/react";
import { Download, TriangleAlert } from "lucide-react";
import {
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";

import { BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  DraftConflictError,
  getDraftDocx,
  saveDraftDocx,
  type DraftDocx,
  type DraftSaveTrigger
} from "../../api/drafts-v2";

import { useDocxAutosave, type Autosave } from "./autosave";
import { DocxEditorBoundary } from "./DocxEditorBoundary";
import {
  loadErrorMessage,
  reconcileDocxSnapshot,
  savedDocxSnapshot,
  type DocxSnapshot
} from "./documentState";
import {
  draftRecoveryKey,
  keepDocxVersion,
  readDocxRecovery,
  removeDocxRecovery,
  writeDocxRecovery,
  type DocxRecovery
} from "./docxRecovery";
import { docxEditorEngine } from "./engine";
import { useOpenDraftsStore } from "./openDrafts";
import { TitleBarNote } from "./TitleBarNote";
import type {
  DocxEditorMode,
  DocxFileMenuItem,
  DocxSelection,
  DocxSurfaceHandle
} from "./surface";

// The editor bundle is large and needs the DOM, so it loads only when an
// attorney actually opens a draft for editing. Which engine loads is decided
// once per mount by the flag (see `engine.ts`).
const DocxEditorSurface = lazy(() =>
  docxEditorEngine() === "legacy"
    ? import("./LegacyDocxEditorSurface")
    : import("./DocxEditorSurface")
);

export type DraftDocxEditorProps = {
  draftId: string;
  /**
   * Bumping this remounts the editor with a fresh fetch, discarding local
   * state. Prefer `reload()` on the handle, which swaps bytes in place.
   */
  reloadKey?: number;
  readOnly?: boolean;
  author?: string;
  /** Controlled editing mode (Editing / Suggesting / Viewing). */
  mode?: DocxEditorMode;
  onModeChange?: (mode: DocxEditorMode) => void;
  /** Controlled visibility of the editor's own review sidebar. */
  commentsSidebarOpen?: boolean;
  onCommentsSidebarOpenChange?: (open: boolean) => void;
  /**
   * Load the docx to open instead of the draft's current body. Used for
   * read-only previews of something other than the live draft (e.g. a version
   * snapshot). When set, saving is not meaningful, so pass `readOnly`.
   */
  loadDocx?: () => Promise<DraftDocx>;
  /**
   * Persist the document somewhere other than the draft API (the QA harness
   * saves in memory). Receives the same `ifMatch`/`trigger` the API would and
   * returns the new revision token; throw `DraftConflictError` to exercise the
   * conflict path. With `loadDocx` set this makes the document a live,
   * editable one rather than a preview.
   */
  saveDocx?: (
    bytes: ArrayBuffer,
    options: { ifMatch?: string | null; trigger?: DraftSaveTrigger }
  ) => Promise<{ updatedAt: string | null; citationsRepointed?: boolean }>;
  /** Called after a successful save, so the host can refresh derived views. */
  onSaved?: () => void;
  /**
   * Save on its own shortly after edits (see `autosave.ts`). Pauses while a
   * notice (recovery, a newer server copy, a conflict) waits on the attorney.
   */
  autosave?: boolean;
  /**
   * Where unsaved edits are checkpointed (see `docxRecovery.ts`). Defaults to
   * the draft's key; a host opening something other than a draft (a case
   * file) passes its own so checkpoints never collide.
   */
  recoveryKey?: string;
  /** Fires as the document goes from saved to edited and back. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Live count of citations to exhibits no longer in the packet. The editor
   * knows the count as of the last load (from the response header); a host
   * watching the filesystem can pass a fresher one so the warning tracks an
   * exhibit removed while the draft is open.
   */
  unresolvedCitations?: number;
  /**
   * Print handler for the editor's File › Print row and Cmd/Ctrl+P. Hosts point
   * it at the server's PDF render so what prints carries current exhibit labels
   * rather than whatever the editor loaded.
   */
  onPrint?: () => void;
  /** Host rows shown first in the editor's File menu (e.g. downloads). */
  fileMenuItems?: DocxFileMenuItem[];
  onClose?: () => void;
  /** The highlighted selection as it moves; `null` on a collapsed caret. */
  onSelectionChange?: (selection: DocxSelection | null) => void;
  /** Draft name shown in the editor's title bar. */
  title?: string;
  /**
   * Persist a new draft name. Passing it makes the title bar's name editable
   * (Google Docs style): edits commit shortly after typing stops, or when the
   * field loses focus. Blank names are never committed.
   */
  onRename?: (title: string) => Promise<void> | void;
  /**
   * Host-owned controls (e.g. version history) rendered in the
   * editor's title bar after the save status.
   */
  titleBarActions?: ReactNode;
  /**
   * A muted line beside the document name, above the menu bar (who generated
   * the draft). Left of the save status, never part of it.
   */
  titleNote?: string | null;
  /** Host-owned strip rendered above the editor (e.g. a pending-changes banner). */
  banner?: ReactNode;
  /**
   * Focus mode: the editor takes the whole window. Only a class changes, so
   * the editor is not remounted and the attorney's zoom and caret survive.
   */
  expanded?: boolean;
  /**
   * Select this text in the document once the editor is up ("Show in
   * document"). `nonce` distinguishes repeat requests for the same text;
   * `onRevealed` reports whether a match was found.
   */
  reveal?: { text: string; changeId?: string; nonce: number } | null;
  onRevealed?: (nonce: number, found: boolean) => void;
  /**
   * Rendered beside the editor with the element that wraps it and the mounted
   * surface (both null until they exist). Hosts use it for overlays that read
   * the editor's painted DOM and model, such as the citation hover card.
   */
  overlay?: (
    host: HTMLElement | null,
    surface: DocxSurfaceHandle | null
  ) => ReactNode;
};

/** What a host can ask of the editor imperatively. */
export type DraftDocxEditorHandle = {
  /**
   * Save the document as the editor's own Save control would. Resolves once
   * the server has it (`onSaved` fires too); rejects if the save failed,
   * with `DraftConflictError` when the server holds a newer revision.
   */
  save: (options?: DraftSaveOptions) => Promise<void>;
  /** The mounted surface, for review and reveal calls; `null` until it is up. */
  surface: () => DocxSurfaceHandle | null;
  /**
   * Fetch the server's copy and swap it into the running editor when it is
   * newer. With unsaved edits it waits behind a notice instead, unless
   * `discardLocal` is set.
   */
  reload: (options?: {
    discardLocal?: boolean;
    /** Take the server's copy even at the revision already held. */
    replace?: boolean;
  }) => Promise<void>;
  /** The editor's current bytes (through the serialization queue). */
  getBuffer: () => Promise<ArrayBuffer | null>;
  /**
   * Swap other bytes (an older version) into the running editor as unsaved
   * changes. Nothing reaches the server until the next save (autosave's, or
   * the attorney's), which still carries the current revision so a newer
   * server copy is caught as a conflict rather than overwritten. `trigger`
   * labels that save, so it starts a version of its own.
   */
  loadUnsaved: (
    bytes: ArrayBuffer,
    options?: { trigger?: DraftSaveTrigger }
  ) => Promise<void>;
  isDirty: () => boolean;
  /** Drop the crash-recovery checkpoint (after the attorney chose to discard). */
  discardRecovery: () => void;
};

const RENAME_DEBOUNCE_MS = 800;

/**
 * Local title state for the editor's name field. The field is controlled and
 * reports every keystroke, so this holds the in-progress value and commits the
 * trimmed name after a pause, on blur, or on unmount. Blank input is kept while
 * typing but reverts to the last committed name rather than being saved.
 *
 * While the field has focus it is the user's: the host's `title` is never
 * written into it, not even the echo of our own commit (which is trimmed and
 * would eat a trailing space mid-sentence). Reconciliation with the host
 * happens on blur, or immediately for external changes while unfocused.
 */
const useTitleEditing = (
  title: string | undefined,
  onRename: ((title: string) => Promise<void> | void) | undefined
) => {
  const [draft, setDraft] = useState(title ?? "");
  const committed = useRef(title ?? "");
  const host = useRef(title ?? "");
  const focused = useRef(false);
  const pending = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const renameRef = useRef(onRename);
  renameRef.current = onRename;

  useEffect(() => {
    const next = title ?? "";
    host.current = next;
    // Unfocused and the host disagrees with what we last saved: an external
    // rename or a rollback after a failed one. Take it.
    if (!focused.current && next !== committed.current) {
      committed.current = next;
      setDraft(next);
    }
  }, [title]);

  const commit = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const value = pending.current;
    pending.current = null;
    if (value === null) return;
    const next = value.trim();
    if (!next || next === committed.current) return;
    committed.current = next;
    // Hosts handle their own failures (see DraftPane); nothing to await here.
    renameRef.current?.(next);
  }, []);

  const change = useCallback(
    (value: string) => {
      setDraft(value);
      pending.current = value;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(commit, RENAME_DEBOUNCE_MS);
    },
    [commit]
  );

  const focus = useCallback(() => {
    focused.current = true;
  }, []);

  const blur = useCallback(() => {
    focused.current = false;
    commit();
    // Settle the field: a host value that differs from what we saved wins
    // (rollback), otherwise show the normalised (trimmed, non-blank) name.
    if (host.current !== committed.current) committed.current = host.current;
    setDraft(committed.current);
  }, [commit]);

  // Flush anything still pending when the editor goes away.
  useEffect(() => commit, [commit]);

  return { draft, change, focus, blur };
};

/** Small warning glyph with the message in a tooltip; keeps the title bar to one line. */
const TitleBarWarning = ({
  message,
  tone
}: {
  message: string;
  tone: "warning" | "error";
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        role="img"
        aria-label={message}
        className={
          tone === "error"
            ? "flex size-7 items-center justify-center text-red-600"
            : "flex size-7 items-center justify-center text-amber-600"
        }
      >
        <TriangleAlert className="size-3.5" />
      </span>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="max-w-64">
      {message}
    </TooltipContent>
  </Tooltip>
);

const EDITOR_FALLBACK = (
  <div className="flex h-full w-full items-center justify-center gap-3">
    <BrandLoader size="xl" label="Loading editor" />
  </div>
);

/**
 * Options for a save. `force` skips the revision check and overwrites whatever
 * the server holds; the attorney chooses it from the conflict notice.
 */
export type DraftSaveOptions = {
  force?: boolean;
  trigger?: DraftSaveTrigger;
  /** Internal: this save is the automatic follow-up to one that raced an edit. */
  followUp?: boolean;
};

/** Which notice sits above the editor, if any. */
type DocumentNotice =
  | { kind: "recovery"; recovery: DocxRecovery }
  | { kind: "server-changed" }
  | { kind: "conflict"; updatedAt: string | null };

const IDENTITY_POLL_MS = 250;
const CHECKPOINT_MS = 5000;

const timeOf = (epochMs: number) =>
  new Date(epochMs).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit"
  });

/** One-line notice strip with actions, rendered above the editor. */
const NoticeBar = ({
  tone,
  message,
  actions
}: {
  tone: "info" | "warning";
  message: string;
  actions: ReactNode;
}) => (
  <div
    role="status"
    data-testid="draft-notice"
    className={
      tone === "warning"
        ? "flex shrink-0 flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
        : "flex shrink-0 flex-wrap items-center gap-2 border-b border-sky-200 bg-sky-50 px-3 py-1.5 text-xs text-sky-900"
    }
  >
    <TriangleAlert className="size-3.5 shrink-0" />
    <span className="min-w-0 flex-1">{message}</span>
    <span className="flex items-center gap-1">{actions}</span>
  </div>
);

/**
 * The in-app editing surface for a draft.
 *
 * A draft is docx end to end: these are the same bytes the download and the
 * packet builder use, edited in place and handed back as docx. There is no
 * intermediate representation to keep in sync, which is the whole reason the
 * HTML draft model is gone.
 *
 * Document state follows one rule: the attorney's unsaved edits are never
 * thrown away by the software. Saves are conditional on the revision that was
 * loaded (`If-Match`), a newer server copy waits behind a notice while there
 * are unsaved edits, unsaved bytes are checkpointed to IndexedDB for recovery,
 * and reloads happen in place (`surface.load`) so the editor never remounts
 * under the attorney.
 */
export const DraftDocxEditor = forwardRef<
  DraftDocxEditorHandle,
  DraftDocxEditorProps
>(function DraftDocxEditor(
  {
    draftId,
    reloadKey = 0,
    readOnly = false,
    author,
    mode,
    onModeChange,
    commentsSidebarOpen,
    onCommentsSidebarOpenChange,
    loadDocx,
    saveDocx,
    onSaved,
    autosave = false,
    recoveryKey: recoveryKeyProp,
    onDirtyChange,
    unresolvedCitations: liveUnresolvedCitations,
    onPrint,
    fileMenuItems,
    onClose,
    onSelectionChange,
    title,
    onRename,
    titleBarActions,
    titleNote,
    banner,
    reveal,
    onRevealed,
    overlay,
    expanded = false
  },
  ref
) {
  const editorRef = useRef<DocxSurfaceHandle | null>(null);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const editable = Boolean(onRename) && !readOnly;
  const titleEditing = useTitleEditing(title, editable ? onRename : undefined);
  // The bytes the surface was mounted with. Later loads go through
  // `surface.load` so the vendor editor is never remounted.
  const [initialDocx, setInitialDocx] = useState<ArrayBuffer | null>(null);
  const snapshot = useRef<DocxSnapshot | null>(null);
  const loadGeneration = useRef(0);
  const [loadedUnresolvedCitations, setLoadedUnresolvedCitations] = useState(0);
  const unresolvedCitations =
    liveUnresolvedCitations ?? loadedUnresolvedCitations;
  const [error, setError] = useState<string | null>(null);
  // The vendor editor could not paginate the loaded bytes (e.g. margins that
  // leave no text area). The surface stays mounted so a reload can recover.
  const [layoutFailed, setLayoutFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [notice, setNotice] = useState<DocumentNotice | null>(null);
  // Flips once the surface has painted; reveal requests wait for it.
  const [editorReady, setEditorReady] = useState(false);
  const [host, setHost] = useState<HTMLElement | null>(null);
  // The mounted surface as state, so overlays re-render when it comes up.
  const [surface, setSurface] = useState<DocxSurfaceHandle | null>(null);

  const isLiveDraft = !loadDocx || Boolean(saveDocx);
  const recoveryKey = recoveryKeyProp ?? draftRecoveryKey(draftId);
  const titleRef = useRef(title);
  titleRef.current = title;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);

  // Local edit counter and change listeners, for the open-drafts registry:
  // the chat's review card and the agent path derive state from the live
  // document and need to know when it moved.
  const revision = useRef(0);
  const checkpointedRevision = useRef(0);
  const dirtyRef = useRef(false);
  dirtyRef.current = isDirty;
  const listeners = useRef(new Set<() => void>());
  const notify = useCallback(() => {
    listeners.current.forEach((listener) => listener());
  }, []);
  const lastIdentity = useRef<unknown>(undefined);
  const autosaver = useRef<Autosave | null>(null);
  const markEdited = useCallback(() => {
    revision.current += 1;
    // The poll below must not count this same edit again.
    lastIdentity.current = editorRef.current?.getDocumentIdentity();
    setIsDirty(true);
    notify();
    autosaver.current?.edited();
  }, [notify]);

  // The editor reports edits through onChange/onCommentsChange, but not every
  // path does (programmatic transactions, some review commands). A cheap
  // identity poll on the document catches those.
  useEffect(() => {
    if (!editorReady || readOnly) return undefined;
    const tick = () => {
      const current = editorRef.current?.getDocumentIdentity();
      if (lastIdentity.current === undefined) {
        lastIdentity.current = current;
        return;
      }
      if (current !== lastIdentity.current) {
        lastIdentity.current = current;
        markEdited();
      }
    };
    const timer = window.setInterval(tick, IDENTITY_POLL_MS);
    return () => window.clearInterval(timer);
  }, [editorReady, readOnly, markEdited]);

  // Every read of the editor's bytes goes through one queue: a save and a
  // download or checkpoint racing each other would otherwise serialise the
  // document twice at once.
  const serialization = useRef<Promise<unknown>>(Promise.resolve());
  const getBuffer = useCallback((): Promise<ArrayBuffer | null> => {
    const next = serialization.current
      .catch(() => undefined)
      .then(() => editorRef.current?.save() ?? null);
    serialization.current = next;
    return next;
  }, []);

  // What the next save is, when bytes from elsewhere replaced the document.
  const unsavedTrigger = useRef<DraftSaveTrigger | null>(null);

  // Baseline after a fresh load: what the editor holds is the saved document.
  const settleLoaded = useCallback(() => {
    unsavedTrigger.current = null;
    revision.current = 0;
    checkpointedRevision.current = 0;
    lastIdentity.current = undefined;
    setIsDirty(false);
    notify();
  }, [notify]);

  // A reveal request waits for the editor to be up, then runs once per nonce.
  const revealedNonce = useRef<number | null>(null);
  const onRevealedRef = useRef(onRevealed);
  onRevealedRef.current = onRevealed;
  useEffect(() => {
    if (!reveal || !editorReady || revealedNonce.current === reveal.nonce)
      return;
    revealedNonce.current = reveal.nonce;
    const surfaceHandle = editorRef.current;
    // The exact revision when the editor still has it; its text once it has
    // been accepted or rejected and only the words remain.
    const changeId = Number(reveal.changeId);
    const found =
      (Number.isInteger(changeId) && surfaceHandle?.revealChange(changeId)) ||
      (surfaceHandle?.revealText(reveal.text) ?? false);
    onRevealedRef.current?.(reveal.nonce, found);
  }, [reveal, editorReady]);

  const loadDocxRef = useRef(loadDocx);
  loadDocxRef.current = loadDocx;
  const saveDocxRef = useRef(saveDocx);
  saveDocxRef.current = saveDocx;
  const fetchDocx = useCallback(
    () => (loadDocxRef.current ?? (() => getDraftDocx(draftId)))(),
    [draftId]
  );

  // Initial load (and hard reload on `reloadKey`): mount the surface with the
  // bytes and, for the live draft, look for an unsaved checkpoint to offer.
  useEffect(() => {
    let cancelled = false;
    setInitialDocx(null);
    snapshot.current = null;
    setEditorReady(false);
    setSurface(null);
    setError(null);
    setLayoutFailed(false);
    setNotice(null);
    setIsDirty(false);

    const recovery = isLiveDraft
      ? readDocxRecovery(recoveryKey)
      : Promise.resolve(null);
    Promise.all([fetchDocx(), recovery])
      .then(([result, recovered]) => {
        if (cancelled) return;
        loadGeneration.current += 1;
        snapshot.current = {
          data: result.bytes,
          updatedAt: result.updatedAt,
          revision: loadGeneration.current
        };
        setInitialDocx(result.bytes);
        setLoadedUnresolvedCitations(result.unresolvedCitations);
        if (recovered && !readOnly)
          setNotice({ kind: "recovery", recovery: recovered });
      })
      .catch((loadError) => {
        if (cancelled) return;
        Sentry.captureException(loadError);
        setError(loadErrorMessage(loadError));
      });

    return () => {
      cancelled = true;
    };
    // `draftId`/`reloadKey` are the cache key so a caller can pass an inline
    // loader without re-fetch loops.
  }, [draftId, reloadKey]);

  /**
   * Fetch the server's copy and, when it is newer and nothing is unsaved,
   * swap it into the running editor. With unsaved edits the newer copy waits
   * behind a notice unless `discardLocal` says otherwise.
   */
  const reload = useCallback(
    async ({
      discardLocal = false,
      replace = false
    }: { discardLocal?: boolean; replace?: boolean } = {}) => {
      const result = await fetchDocx();
      loadGeneration.current += 1;
      const incoming: DocxSnapshot = {
        data: result.bytes,
        updatedAt: result.updatedAt,
        revision: loadGeneration.current
      };
      const chosen =
        replace && !dirtyRef.current
          ? incoming
          : reconcileDocxSnapshot(
              snapshot.current,
              incoming,
              dirtyRef.current && !discardLocal
            );
      if (chosen !== incoming) {
        // Same document, or the attorney's edits win for now. Only say
        // something when the server really moved on.
        if (
          dirtyRef.current &&
          snapshot.current &&
          (snapshot.current.updatedAt === null ||
            snapshot.current.updatedAt !== incoming.updatedAt)
        ) {
          setNotice((current) =>
            current?.kind === "conflict" ? current : { kind: "server-changed" }
          );
        }
        return;
      }
      snapshot.current = incoming;
      setLoadedUnresolvedCitations(result.unresolvedCitations);
      setNotice((current) => (current?.kind === "recovery" ? current : null));
      const surfaceHandle = editorRef.current;
      setLayoutFailed(false);
      if (surfaceHandle) {
        await surfaceHandle.load(incoming.data);
        settleLoaded();
      } else {
        // Not mounted yet; mount with the fresher bytes.
        setInitialDocx(incoming.data);
      }
      if (discardLocal) void removeDocxRecovery(recoveryKey);
    },
    [fetchDocx, recoveryKey, settleLoaded]
  );

  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const citationsBehind = useRef(false);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const pendingSave = useRef<Promise<void> | null>(null);
  const saveRef = useRef<
    ((options?: DraftSaveOptions) => Promise<void>) | null
  >(null);
  const handleSave = useCallback(
    (options: DraftSaveOptions = {}): Promise<void> => {
      // One save at a time; a second request while one is in flight joins it.
      if (pendingSave.current && !options.force) return pendingSave.current;
      const run = (async () => {
        const savingRevision = revision.current;
        const pendingTrigger = unsavedTrigger.current;
        const bytes = await getBuffer();
        if (!bytes) return;
        setIsSaving(true);
        setError(null);
        try {
          const saveOptions = {
            ifMatch: options.force ? null : snapshot.current?.updatedAt,
            trigger: options.trigger ?? pendingTrigger ?? undefined
          };
          const saved = saveDocxRef.current
            ? await saveDocxRef.current(bytes, saveOptions)
            : await saveDraftDocx(draftId, bytes, saveOptions);
          snapshot.current = snapshot.current
            ? savedDocxSnapshot(snapshot.current, bytes, saved.updatedAt)
            : {
                data: bytes,
                updatedAt: saved.updatedAt,
                revision: loadGeneration.current
              };
          if (unsavedTrigger.current === pendingTrigger)
            unsavedTrigger.current = null;
          void keepDocxVersion(recoveryKey, bytes);
          if (saved.citationsRepointed) citationsBehind.current = true;
          if (revision.current === savingRevision) {
            // Nothing typed while the save was in flight: clean.
            setIsDirty(false);
            checkpointedRevision.current = revision.current;
            void removeDocxRecovery(recoveryKey);
            if (citationsBehind.current) {
              // The server re-pointed citations in what it stored; show them.
              // Typing that raced the save waits for the next clean save.
              citationsBehind.current = false;
              void reloadRef
                .current?.({ replace: true })
                .catch(() => undefined);
            }
          } else if (!options.followUp) {
            // An edit landed while the bytes were in flight (typing, or the
            // editor's own deferred transaction after a card action). The
            // attorney asked for a save, so save once more rather than leave
            // "Unsaved changes" standing over a document that is nearly saved.
            window.setTimeout(() => {
              void saveRef
                .current?.({ ...options, followUp: true })
                .catch(() => undefined);
            }, 0);
          }
          setNotice((current) =>
            current?.kind === "recovery" ? current : null
          );
          notify();
          onSavedRef.current?.();
        } catch (saveError) {
          if (saveError instanceof DraftConflictError) {
            setNotice({ kind: "conflict", updatedAt: saveError.updatedAt });
            setError(null);
          } else {
            Sentry.captureException(saveError);
            setError(
              "Couldn't save your changes. They are still in the editor."
            );
          }
          throw saveError;
        } finally {
          setIsSaving(false);
        }
      })();
      pendingSave.current = run;
      run.finally(() => {
        if (pendingSave.current === run) pendingSave.current = null;
      });
      return run;
    },
    [draftId, getBuffer, notify, recoveryKey]
  );
  saveRef.current = handleSave;

  // Every notice waits on a decision a save would preempt: recovering (a save
  // drops the checkpoint), a newer server copy, or a refused save.
  const autosaveOn = autosave && isLiveDraft && !readOnly;
  const autosavePaused = notice !== null;
  autosaver.current = useDocxAutosave({
    enabled: autosaveOn,
    paused: autosavePaused,
    isDirty: () => dirtyRef.current,
    // The loop reschedules for edits that race a save; no follow-up needed.
    save: () => handleSave({ followUp: true }),
    isFatal: (saveError) => saveError instanceof DraftConflictError
  });

  // Crash recovery: checkpoint unsaved bytes every few seconds, when the tab
  // hides, and on unmount. Only for the live draft, never for previews.
  const checkpoint = useCallback(async () => {
    if (!isLiveDraft || readOnly || !dirtyRef.current) return;
    if (checkpointedRevision.current === revision.current) return;
    const at = revision.current;
    const bytes = await getBuffer();
    // A save may have landed meanwhile; a checkpoint must not undo it.
    if (!bytes || !dirtyRef.current || revision.current !== at) return;
    checkpointedRevision.current = at;
    await writeDocxRecovery({
      key: recoveryKey,
      buffer: bytes,
      baseUpdatedAt: snapshot.current?.updatedAt ?? null,
      savedAt: Date.now()
    });
  }, [getBuffer, isLiveDraft, readOnly, recoveryKey]);
  const checkpointRef = useRef(checkpoint);
  checkpointRef.current = checkpoint;
  useEffect(() => {
    if (!isLiveDraft || readOnly) return undefined;
    const timer = window.setInterval(() => {
      void checkpointRef.current();
    }, CHECKPOINT_MS);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") void checkpointRef.current();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      void checkpointRef.current();
    };
  }, [isLiveDraft, readOnly]);

  const recoverDraft = useCallback(async () => {
    if (notice?.kind !== "recovery") return;
    const surfaceHandle = editorRef.current;
    if (!surfaceHandle) return;
    await surfaceHandle.load(notice.recovery.buffer);
    lastIdentity.current = undefined;
    setNotice(null);
    markEdited();
  }, [markEdited, notice]);

  const discardRecovery = useCallback(() => {
    setNotice(null);
    void removeDocxRecovery(recoveryKey);
  }, [recoveryKey]);

  const loadUnsaved = useCallback(
    async (
      bytes: ArrayBuffer,
      options: { trigger?: DraftSaveTrigger } = {}
    ) => {
      const surfaceHandle = editorRef.current;
      if (!surfaceHandle) return;
      await surfaceHandle.load(bytes.slice(0));
      unsavedTrigger.current = options.trigger ?? null;
      lastIdentity.current = undefined;
      setNotice((current) => (current?.kind === "recovery" ? null : current));
      markEdited();
    },
    [markEdited]
  );

  const downloadBytes = useCallback(
    (bytes: ArrayBuffer, suffix: string) => {
      const url = URL.createObjectURL(
        new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        })
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${(title ?? "draft").replace(
        /[\\/:*?"<>|]+/g,
        "-"
      )}${suffix}.docx`;
      anchor.click();
      URL.revokeObjectURL(url);
    },
    [title]
  );

  const downloadCopy = useCallback(async () => {
    const bytes = await getBuffer();
    if (!bytes) return;
    downloadBytes(bytes, " (my copy)");
  }, [getBuffer, downloadBytes]);

  const layoutFailedRef = useRef(false);
  layoutFailedRef.current = layoutFailed;
  const handleSurfaceError = useCallback((surfaceError: Error) => {
    if (surfaceError.name === "DocxLayoutError") {
      // Layout reruns on every change; report the first failure per load.
      if (!layoutFailedRef.current) Sentry.captureException(surfaceError);
      layoutFailedRef.current = true;
      setLayoutFailed(true);
      return;
    }
    Sentry.captureException(surfaceError);
    setError(surfaceError.message);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      surface: () => editorRef.current,
      reload,
      getBuffer,
      loadUnsaved,
      isDirty: () => dirtyRef.current,
      discardRecovery: () => {
        void removeDocxRecovery(recoveryKey);
      }
    }),
    [handleSave, reload, getBuffer, loadUnsaved, recoveryKey]
  );

  // Announce the live draft to whoever needs the open editor. Previews of
  // other bytes (version history) are not the draft and stay unregistered.
  useEffect(() => {
    if (!isLiveDraft || !editorReady) return undefined;
    return useOpenDraftsStore.getState().register({
      draftId,
      surface: () => editorRef.current,
      save: (options) => handleSave(options),
      revision: () => revision.current,
      isDirty: () => dirtyRef.current,
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => {
          listeners.current.delete(listener);
        };
      },
      readOnly,
      title: () => titleRef.current ?? "",
      mode: () => modeRef.current ?? "editing"
    });
  }, [draftId, editorReady, handleSave, isLiveDraft, readOnly]);

  // The editor's own Save control reports failure in the title bar; it does
  // not want the rejection.
  const saveFromEditor = useCallback(() => {
    handleSave().catch(() => undefined);
  }, [handleSave]);

  // With autosave, edits read as "Saving…" until they land, as in Google
  // Docs; "Unsaved changes" means autosave is waiting (a notice, a failure).
  const saveStatus = isSaving
    ? "Saving…"
    : notice?.kind === "conflict"
    ? "Not saved"
    : isDirty
    ? autosaveOn && !autosavePaused && !error
      ? "Saving…"
      : "Unsaved changes"
    : "All changes saved";

  // Everything that used to sit in a row above the editor now lives in the
  // editor's own title bar, to the right of the document name.
  const titleBarRight = useMemo(() => {
    const hasContent =
      !readOnly ||
      unresolvedCitations > 0 ||
      error ||
      titleBarActions ||
      onClose;
    if (!hasContent) return null;
    return (
      <div className="flex items-center gap-0.5">
        {readOnly ? null : (
          <span
            data-testid="draft-save-status"
            className="px-1.5 text-xs whitespace-nowrap text-muted-foreground select-none"
          >
            {saveStatus}
          </span>
        )}
        {unresolvedCitations > 0 ? (
          <TitleBarWarning
            tone="warning"
            message={
              unresolvedCitations === 1
                ? "1 citation points at an exhibit removed from the packet. It reads “[exhibit removed]” until the citation is fixed."
                : `${unresolvedCitations} citations point at exhibits removed from the packet. They read “[exhibit removed]” until the citations are fixed.`
            }
          />
        ) : null}
        {error ? <TitleBarWarning tone="error" message={error} /> : null}
        {titleBarActions}
        {onClose ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={onClose}
          >
            Close
          </Button>
        ) : null}
      </div>
    );
  }, [
    readOnly,
    unresolvedCitations,
    error,
    saveStatus,
    titleBarActions,
    onClose
  ]);

  const noticeBar = (() => {
    if (!notice || readOnly) return null;
    if (notice.kind === "recovery") {
      const { recovery } = notice;
      const baseMoved =
        recovery.baseUpdatedAt !== null &&
        snapshot.current?.updatedAt !== null &&
        recovery.baseUpdatedAt !== snapshot.current?.updatedAt;
      return (
        <NoticeBar
          tone="info"
          message={`Recover your unsaved edits from ${timeOf(
            recovery.savedAt
          )}?${
            baseMoved
              ? " The draft has been saved since then; recovering shows your edits on top of the older copy."
              : ""
          }`}
          actions={
            <>
              <Button
                variant="primary"
                size="sm"
                className="h-6 text-xs"
                disabled={!editorReady}
                onClick={() => {
                  void recoverDraft();
                }}
              >
                Recover
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={discardRecovery}
              >
                Discard
              </Button>
            </>
          }
        />
      );
    }
    if (notice.kind === "server-changed") {
      return (
        <NoticeBar
          tone="warning"
          message="This draft was saved elsewhere (the agent, Word, or another editor) while you have unsaved edits. Yours are kept until you decide."
          actions={
            <>
              <Button
                variant="primary"
                size="sm"
                className="h-6 text-xs"
                onClick={() => {
                  handleSave({ force: true }).catch(() => undefined);
                }}
              >
                Keep mine and save
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs"
                onClick={() => {
                  reload({ discardLocal: true }).catch(() => undefined);
                }}
              >
                Load theirs, discard mine
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={() => setNotice(null)}
              >
                Later
              </Button>
            </>
          }
        />
      );
    }
    return (
      <NoticeBar
        tone="warning"
        message="Your save was refused: the draft changed on the server since you loaded it. Nothing was overwritten; your edits are still in the editor."
        actions={
          <>
            <Button
              variant="primary"
              size="sm"
              className="h-6 text-xs"
              onClick={() => {
                handleSave({ force: true }).catch(() => undefined);
              }}
            >
              Overwrite with mine
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-xs"
              onClick={() => {
                reload({ discardLocal: true }).catch(() => undefined);
              }}
            >
              Load theirs, discard mine
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={() => {
                void downloadCopy();
              }}
            >
              Download my copy
            </Button>
          </>
        }
      />
    );
  })();

  if (error && !initialDocx) {
    return (
      <div className="flex h-full w-full items-center justify-center px-6">
        <span className="text-body font-body text-subtext-color">{error}</span>
      </div>
    );
  }

  if (!initialDocx) return EDITOR_FALLBACK;

  return (
    // Focus events bubble up from the title bar's name field. Other inputs in
    // the chrome (font size, zoom) also match, but for them focus/blur is a
    // harmless no-op: nothing is pending and the field already shows the
    // committed name.
    <div
      ref={setRoot}
      className={
        expanded
          ? "docx-draft-focus flex min-h-0 flex-col"
          : "flex h-full min-h-0 w-full flex-col"
      }
      data-draft-expanded={expanded || undefined}
      onFocus={
        editable
          ? (e) => {
              if (e.target instanceof HTMLInputElement) titleEditing.focus();
            }
          : undefined
      }
      onBlur={
        editable
          ? (e) => {
              if (e.target instanceof HTMLInputElement) titleEditing.blur();
            }
          : undefined
      }
    >
      {banner ? <div className="shrink-0">{banner}</div> : null}
      {noticeBar}
      <div className="relative min-h-0 flex-1">
        {layoutFailed ? (
          <div
            role="alert"
            className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background px-6 text-center"
          >
            <span className="text-body-bold font-body-bold text-default-font">
              This document could not be laid out
            </span>
            <span className="max-w-md text-body font-body text-subtext-color">
              Its page setup leaves the editor no room to place text. Download
              it to open in Word, or ask the assistant to fix the page setup.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const bytes = snapshot.current?.data;
                if (bytes) downloadBytes(bytes, "");
              }}
            >
              <Download className="size-3.5" />
              Download
            </Button>
          </div>
        ) : null}
        <DocxEditorBoundary>
          <Suspense fallback={EDITOR_FALLBACK}>
            <DocxEditorSurface
              ref={editorRef}
              document={initialDocx}
              readOnly={readOnly}
              author={author}
              mode={mode}
              onModeChange={onModeChange}
              commentsSidebarOpen={commentsSidebarOpen}
              onCommentsSidebarOpenChange={onCommentsSidebarOpenChange}
              title={editable ? titleEditing.draft : title}
              onTitleChange={editable ? titleEditing.change : undefined}
              titleBarRight={titleBarRight}
              onChange={markEdited}
              onCommentsChange={markEdited}
              onSave={saveFromEditor}
              onPrint={onPrint}
              fileMenuItems={fileMenuItems}
              onSelectionChange={onSelectionChange}
              onReady={() => {
                setSurface(editorRef.current);
                setEditorReady(true);
              }}
              onHostChange={overlay ? setHost : undefined}
              onError={handleSurfaceError}
            />
          </Suspense>
        </DocxEditorBoundary>
      </div>
      {overlay ? overlay(host, surface) : null}
      {titleNote ? (
        <TitleBarNote container={root}>
          <span
            className="truncate text-sm whitespace-nowrap text-fg-3 select-none"
            data-testid="draft-provenance"
            title={titleNote}
          >
            {titleNote}
          </span>
        </TitleBarNote>
      ) : null}
    </div>
  );
});

export default DraftDocxEditor;
